import React, { createContext, useEffect, useMemo, useState } from 'react';

import type { LatLng, SatellitePlace } from '../@types';
import { PlaceSearchStateEnum } from '../components/sections/sectionGlobalSearch/types';
import type { KioskRegistration } from '../services/types/kioskRegistration';
import { ensureHttps } from '../utils/url';
import type { Building, Floor, Place, PlaceGroup, Site } from './types';

// eslint-disable-next-line no-undef
type PlaceResult = any;

const maxLocationsItems = 12;
const defaultScreenState = PlaceSearchStateEnum.DISPLAYCATEGORIES;

export interface AppState {
  globalSearchText?: string;
  setGlobalSearchText: (globalSearchText: string | undefined) => void;
  campusName?: string;
  setCampusName: (campusName: string | undefined) => void;
  selectedSite?: Site;
  availableSites?: Site[];
  setAvailableSites: (availableSites: Site[]) => void;
  setSelectedSite: (selectedSiteId: Site | undefined) => void;
  selectedBuilding?: Building;
  setSelectedBuilding: (selectedBuilding: Building | undefined) => void;
  selectedFloor?: Floor;
  setSelectedFloor: (selectedFloor: Floor | undefined) => void;
  selectedCategory?: PlaceGroup;
  setSelectedCategory: (selectedCategory: PlaceGroup | undefined) => void;
  selectedLocation?: Place;
  setSelectedLocation: (selectedLocation: Place | undefined) => void;
  map?: string;
  setMap: (map: string | undefined) => void;
  showCTAModal?: boolean;
  setShowCTAModal: (showCTAModal: boolean) => void;
  showKeyboard?: boolean;
  setShowKeyboard: (showKeyboard: boolean) => void;
  showAppBanner?: boolean;
  isCallToActionEnabled?: boolean;
  setShowCTASection: (showCTAModal: boolean) => void;
  showCallToAction?: boolean;
  maxLocationsItems: number;
  placeSearchState: PlaceSearchStateEnum;
  setPlaceSearchState: (arg: PlaceSearchStateEnum) => void;
  navStartingPlace?:
    | PlaceResult
    | Place
    | LatLng
    | KioskRegistration
    | SatellitePlace
    | any // AZURE
    | undefined;
  setNavStartingPlace: (
    place:
      | PlaceResult
      | Place
      | LatLng
      | KioskRegistration
      | SatellitePlace
      | undefined,
  ) => void;
  navDestinationPlace?: Place | SatellitePlace;
  setNavDestinationPlace: (place: Place | SatellitePlace | undefined) => void;
  mapLoading: boolean;
  setMapLoading: (mapLoading: boolean) => void;
  // AZURE
  directions?: any;
  setDirections: (directions: any) => void;
  addressSearchResult?: any;
  setAddressSearchResult: (addressSearchResult: any) => void;
  showPrintDirectionView?: boolean;
  setShowPrintDirectionView: (showPrintDirectionView: boolean) => void;
  legTwoFocusedStep?: number;
  setLegTwoFocusedStep: (step: number) => void;
  mapIsDragging: boolean;
  setMapIsDragging: (mapIsDragging: boolean) => void;
  legToggle: boolean;
  setLegToggle: (value: boolean) => void;
  hideNavCTASection?: boolean;
  setHideNavCTASection: (hideNavCTASection: boolean) => void;
  locationSearchText?: string | undefined;
  setLocationSearchText: (locationSearchText: string | undefined) => void;
  tutorialStep?: number;
  setTutorialStep: (step: number | undefined) => void;
  mapZoomLevel: number;
  setMapZoomLevel: (mapZoomLevel: number) => void;
  isUsingCurrentLocation: boolean;
  setIsUsingCurrentLocation: (isUsingCurrentLocation: boolean) => void;
}

export const AppProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [campusName, setCampusName] = useState<string>();
  const [availableSites, setAvailableSites] = useState<Site[]>();
  const [selectedSite, setSelectedSite] = useState<Site | undefined>(undefined);
  const [selectedBuilding, setSelectedBuilding] = useState<Building>();
  const [selectedFloor, setSelectedFloor] = useState<Floor>();
  const [selectedCategory, setSelectedCategory] = useState<PlaceGroup>();
  const [selectedLocation, setSelectedLocation] = useState<Place>();
  const [map, setMap] = useState<string>();
  const [mapIsDragging, setMapIsDragging] = useState<boolean>(false);
  const [globalSearchText, setGlobalSearchText] = useState<string>();
  const [locationSearchText, setLocationSearchText] = useState<string>();
  const [placeSearchState, setPlaceSearchState] =
    useState<PlaceSearchStateEnum>(defaultScreenState);
  const [navStartingPlace, setNavStartingPlace] = useState<
    | PlaceResult
    | Place
    | LatLng
    | KioskRegistration
    | SatellitePlace
    | undefined
  >(undefined);

  const [navDestinationPlace, setNavDestinationPlace] =
    // eslint-disable-next-line no-undef
    useState<Place | SatellitePlace | undefined>(undefined);

  const [mapLoading, setMapLoading] = useState<boolean>(false);
  const [showPrintDirectionView, setShowPrintDirectionView] =
    useState<boolean>(false);
  const [legTwoFocusedStep, setLegTwoFocusedStep] = useState<number>(0);

  // AZURE
  const [directions, setDirections] = useState<any>();

  const [addressSearchResult, setAddressSearchResult] = useState<any>();

  const [legToggle, setLegToggle] = useState(false);
  const [hideNavCTASection, setHideNavCTASection] = useState(false);
  const [tutorialStep, setTutorialStep] = useState<number | undefined>(0);
  const [mapZoomLevel, setMapZoomLevel] = useState<number>(0);

  const [isUsingCurrentLocation, setIsUsingCurrentLocation] =
    useState<boolean>(false);

  useEffect(() => {
    if (selectedSite) {
      setMap(ensureHttps(selectedSite?.campus_map.map_url));
    }
  }, [selectedSite]);

  const kioskMode = process.env.REACT_APP_KIOSK_MODE === 'true';

  const [showCTAModal, setShowCTAModal] = useState<boolean>(false);
  const [showKeyboard, setShowKeyboard] = useState<boolean>(false);
  const [callToActionEnabled, setCallToActionEnabled] =
    useState<boolean>(false);

  const showCallToAction = useMemo(
    () => kioskMode && callToActionEnabled,
    [callToActionEnabled, kioskMode],
  );

  return useMemo(
    () => (
      <AppContext.Provider
        value={{
          campusName,
          setCampusName,
          selectedSite,
          setSelectedSite,
          availableSites,
          setAvailableSites,
          selectedBuilding,
          setSelectedBuilding,
          selectedFloor,
          setSelectedFloor,
          map,
          setMap,
          globalSearchText,
          setGlobalSearchText,
          selectedCategory,
          setSelectedCategory,
          selectedLocation,
          setSelectedLocation,
          showCTAModal,
          setShowCTAModal,
          showKeyboard: showKeyboard && kioskMode,
          setShowKeyboard,
          isCallToActionEnabled: callToActionEnabled,
          setShowCTASection: setCallToActionEnabled,
          showCallToAction: showCallToAction,
          maxLocationsItems,
          navStartingPlace,
          setNavStartingPlace,
          navDestinationPlace,
          setNavDestinationPlace,
          placeSearchState,
          setPlaceSearchState,
          mapLoading,
          setMapLoading,
          showPrintDirectionView,
          setShowPrintDirectionView,
          legTwoFocusedStep,
          setLegTwoFocusedStep,
          mapIsDragging,
          setMapIsDragging,
          legToggle,
          setLegToggle,
          hideNavCTASection,
          setHideNavCTASection,
          locationSearchText,
          setLocationSearchText,
          tutorialStep,
          setTutorialStep,
          mapZoomLevel,
          setMapZoomLevel,
          isUsingCurrentLocation,
          setIsUsingCurrentLocation,
          // AZURE
          directions,
          setDirections,
          addressSearchResult,
          setAddressSearchResult,
        }}
      >
        {children}
      </AppContext.Provider>
    ),
    [
      campusName,
      selectedSite,
      availableSites,
      selectedBuilding,
      selectedFloor,
      map,
      globalSearchText,
      selectedCategory,
      selectedLocation,
      showCTAModal,
      showKeyboard,
      kioskMode,
      callToActionEnabled,
      showCallToAction,
      navStartingPlace,
      navDestinationPlace,
      placeSearchState,
      mapLoading,
      showPrintDirectionView,
      legTwoFocusedStep,
      mapIsDragging,
      legToggle,
      hideNavCTASection,
      locationSearchText,
      tutorialStep,
      mapZoomLevel,
      isUsingCurrentLocation,
      directions,
      addressSearchResult,
      children,
    ],
  );
};

export const AppContext = createContext<AppState>({
  setCampusName(campusName: string | undefined): void {},
  setMap(map: string | undefined): void {},
  setSelectedBuilding(selectedBuilding: Building | undefined): void {},
  setSelectedCategory(selectedCategory: PlaceGroup | undefined): void {},
  setSelectedFloor(selectedFloor: Floor | undefined): void {},
  setSelectedLocation(selectedLocation: Place | undefined): void {},
  setSelectedSite(selectedSite: Site | undefined): void {},
  setAvailableSites(availableSites: Site[] | undefined): void {},
  setGlobalSearchText(globalSearchText: string | undefined): void {},
  setShowCTAModal(showCTAModal: boolean): void {},
  setShowKeyboard(showKeyboard: boolean): void {},
  setShowCTASection(showCTAModal: boolean): void {},
  setPlaceSearchState(screenState: PlaceSearchStateEnum): void {},
  setNavStartingPlace(): void {},
  setNavDestinationPlace(): void {},
  maxLocationsItems,
  placeSearchState: defaultScreenState,
  mapLoading: false,
  setMapLoading(mapLoading: boolean): void {},
  showPrintDirectionView: false,
  setShowPrintDirectionView(showPrintDirectionView: boolean): void {},
  setLegTwoFocusedStep(legTwoFocusedStep: number): void {},
  mapIsDragging: false,
  setMapIsDragging(mapIsDragging: boolean): void {},
  legToggle: false,
  setLegToggle(value: boolean): void {},
  hideNavCTASection: false,
  setHideNavCTASection(hideNavCTASection: boolean): void {},
  setLocationSearchText(locationSearchText: string | undefined): void {},
  tutorialStep: undefined,
  setTutorialStep(step: number | undefined): void {},
  mapZoomLevel: 1,
  setMapZoomLevel(mapZoomLevel: number): void {},
  isUsingCurrentLocation: false,
  setIsUsingCurrentLocation(isUsingCurrentLocation: boolean): void {},
  // AZURE
  setDirections(): void {},
  setAddressSearchResult(addressSearchResult: any): void {},
});
